import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { GraduationCap, Code, Heart, Target, Users, Lightbulb } from 'lucide-react';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const highlights = [
    {
      icon: GraduationCap,
      title: "B.Tech Student",
      description: "Computer Science at Chandigarh Group of Colleges",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: Code,
      title: "Full-Stack Developer",
      description: "Expertise in C++, Python, and modern web technologies",
      color: "text-green-500 dark:text-green-400"
    },
    {
      icon: Heart,
      title: "Problem Solver",
      description: "Passionate about creating real-world impact through technology",
      color: "text-red-500 dark:text-red-400"
    },
    {
      icon: Target,
      title: "Goal-Oriented",
      description: "Actively seeking internship opportunities to grow and contribute",
      color: "text-purple-600 dark:text-purple-400"
    }
  ];

  const achievements = [
    {
      icon: Users,
      title: "Smart India Hackathon",
      description: "Volunteer and technical contributor"
    },
    {
      icon: Lightbulb,
      title: "Tech Fest Organizer",
      description: "Co-organized college technology festivals"
    },
    {
      icon: Code,
      title: "Open Source",
      description: "Contributing to meaningful projects"
    }
  ];

  return (
    <section id="about" ref={sectionRef} className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            About <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Me</span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            My journey in tech is fueled by an insatiable curiosity to learn and build
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Main Content */}
          <div className={`space-y-6 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                As a B.Tech student at <span className="font-semibold text-blue-600 dark:text-cyan-400">Chandigarh Group of Colleges</span>, 
                I've developed expertise in C++, Python, and web technologies while working on diverse projects 
                that span cultural preservation to AI-powered career guidance.
              </p>
              
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                What sets me apart is my <span className="font-semibold text-green-600 dark:text-green-400">hands-on approach</span> – 
                I don't just study algorithms, I implement them. I don't just learn about hardware, 
                I build with Arduino and Raspberry Pi. This practical mindset has led me to volunteer 
                at Smart India Hackathon, co-organize tech fests, and continuously push the boundaries of what I can create.
              </p>

              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                I'm particularly drawn to projects that have <span className="font-semibold text-purple-600 dark:text-purple-400">real-world impact</span>, 
                whether it's preserving cultural heritage through technology or making career guidance 
                more accessible through AI.
              </p>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 p-6 rounded-xl border border-blue-100 dark:border-blue-800">
              <p className="text-slate-700 dark:text-slate-300 font-medium">
                🚀 I'm actively seeking <span className="text-blue-600 dark:text-cyan-400 font-bold">internship opportunities</span> where 
                I can contribute to meaningful projects while learning from experienced professionals 
                and growing my technical expertise in a collaborative environment.
              </p>
            </div>
          </div>

          {/* Highlights */}
          <div className="space-y-6">
            {highlights.map((highlight, index) => (
              <Card 
                key={highlight.title}
                className={`transition-all duration-1000 hover:shadow-lg hover:scale-105 ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
                style={{ transitionDelay: `${300 + index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg bg-slate-100 dark:bg-slate-800 ${highlight.color}`}>
                      <highlight.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                        {highlight.title}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-300">
                        {highlight.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Achievements Section */}
        <div className={`mt-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-8 text-center">
            Key Achievements & Contributions
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {achievements.map((achievement, index) => (
              <Card 
                key={achievement.title}
                className={`text-center hover:shadow-lg transition-all duration-300 hover:scale-105 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${600 + index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="p-4 rounded-full bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30">
                      <achievement.icon className="w-8 h-8 text-blue-600 dark:text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                        {achievement.title}
                      </h4>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        {achievement.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Technologies Badge Section */}
        <div className={`mt-12 text-center transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
            Technologies I Work With
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {['C++', 'Python', 'JavaScript', 'HTML/CSS', 'Arduino', 'Raspberry Pi', 'Git', 'Machine Learning', 'AI'].map((tech, index) => (
              <Badge 
                key={tech}
                variant="secondary"
                className={`px-4 py-2 text-sm font-medium bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 text-blue-700 dark:text-cyan-300 border-blue-200 dark:border-blue-700 hover:scale-110 transition-transform duration-200`}
                style={{ animationDelay: `${800 + index * 50}ms` }}
              >
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;